import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Copy, Mail, MessageSquare, Check, Loader, Globe, Eye, EyeOff } from 'lucide-react';
import { useUserData } from '../hooks/useApi';
import { AppLang } from '../i18n';
import './AccessPage.css';

interface AccessPageProps {
  user: any;
  lang: AppLang;
  subUrl: string;
  onBack: () => void;
}

export const AccessPage = ({ user, lang: _lang, subUrl: _subUrl, onBack }: AccessPageProps) => {
  void _lang; void _subUrl;
  const [copiedLink, setCopiedLink] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Web access (set-password) state
  const [showWebForm, setShowWebForm] = useState(false);
  const [webEmail, setWebEmail] = useState('');
  const [webPassword, setWebPassword] = useState('');
  const [webSaving, setWebSaving] = useState(false);
  const [webDone, setWebDone] = useState(false);
  const [webError, setWebError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { data: userData } = useUserData();
  const userEmail = userData?.email || user?.email;
  const hasWebAccess = !!userEmail;
  const cabinetLink = `https://patrik-service.com`;

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(cabinetLink).catch(() => {});
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  }, [cabinetLink]);

  const handleSetWebAccess = useCallback(async () => {
    if (!webEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(webEmail)) {
      setWebError('Введите корректный email');
      return;
    }
    if (webPassword.length < 6) {
      setWebError('Пароль минимум 6 символов');
      return;
    }
    setWebError('');
    setWebSaving(true);
    try {
      const tg = (window as any).Telegram?.WebApp;
      const initData = tg?.initData ?? '';
      const resp = await fetch('/web-api/auth/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `tma ${initData}` },
        body: JSON.stringify({ email: webEmail, password: webPassword }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed');
      setWebDone(true);
      setShowWebForm(false);
    } catch (e: any) {
      const msg = e.message || '';
      setWebError(msg.includes('уже используется') ? msg : msg.includes('минимум') ? msg : 'Не удалось сохранить. Попробуйте позже.');
    } finally {
      setWebSaving(false);
    }
  }, [webEmail, webPassword]);

  const openTelegramChannel = useCallback(() => {
    const tg = (window as any).Telegram?.WebApp;
    const url = 'https://t.me/Patrik_vpn_support';
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
  }, []);

  const handleSaveEmail = useCallback(async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Введите корректный email');
      return;
    }
    setEmailError('');
    setEmailSaving(true);
    try {
      const tg = (window as any).Telegram?.WebApp;
      const initData = tg?.initData ?? '';
      const resp = await fetch('/app-api/user/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `tma ${initData}` },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) throw new Error('Failed');
      setEmailSaved(true);
      setShowEmailForm(false);
    } catch {
      setEmailError('Не удалось сохранить. Попробуйте позже.');
    } finally {
      setEmailSaving(false);
    }
  }, [email]);

  return (
    <div className="ap">
      <div className="ap-header">
        <button className="ap-back" onClick={onBack}><ArrowLeft size={20} /></button>
      </div>

      <motion.div className="ap-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {/* Hero */}
        <div className="ap-hero">
          <div className="ap-hero-icon"><ShieldCheck size={24} /></div>
          <h2 className="ap-hero-title">Сохранение доступа</h2>
          <p className="ap-hero-sub">
            Чтобы входить в личный кабинет без Telegram — по email и паролю на сайте
          </p>
        </div>

        {/* Step 1: Telegram channel */}
        <div className="ap-step">
          <div className="ap-step-icon"><MessageSquare size={20} /></div>
          <div className="ap-step-info">
            <h3 className="ap-step-title">Подпишитесь на Telegram-канал</h3>
            <p className="ap-step-desc">Для получения важной информации</p>
          </div>
          <button className="ap-step-btn" onClick={openTelegramChannel}>
            Подписаться на канал
          </button>
        </div>

        {/* Step 2: Email */}
        <div className="ap-step">
          <div className="ap-step-icon"><Mail size={20} /></div>
          <div className="ap-step-info">
            <h3 className="ap-step-title">Оставьте адрес электронной почты</h3>
            <p className="ap-step-desc">Будем всегда на связи</p>
          </div>
          {emailSaved ? (
            <div className="ap-email-saved">
              <Check size={16} /> Сохранено
            </div>
          ) : !showEmailForm ? (
            <button className="ap-step-btn" onClick={() => setShowEmailForm(true)}>
              Оставить e-mail
            </button>
          ) : null}
        </div>

        {/* Email form */}
        {showEmailForm && !emailSaved && (
          <motion.div className="ap-email-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
            <input
              type="email"
              className="ap-email-input"
              placeholder="your@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(''); }}
            />
            {emailError && <span className="ap-email-error">{emailError}</span>}
            <button className="ap-email-submit" onClick={handleSaveEmail} disabled={emailSaving}>
              {emailSaving ? <Loader size={16} className="spin" /> : 'Сохранить'}
            </button>
          </motion.div>
        )}

        {/* Step 3: Web access */}
        <div className="ap-step">
          <div className="ap-step-icon"><Globe size={20} /></div>
          <div className="ap-step-info">
            <h3 className="ap-step-title">Веб-доступ к личному кабинету</h3>
            <p className="ap-step-desc">
              {hasWebAccess || webDone ? 'Вход через браузер настроен' : 'Войти через patrik-service.com без Telegram'}
            </p>
          </div>
          {(hasWebAccess || webDone) ? (
            <div className="ap-email-saved">
              <Check size={16} /> Настроено
            </div>
          ) : !showWebForm ? (
            <button className="ap-step-btn" onClick={() => setShowWebForm(true)}>
              Настроить
            </button>
          ) : null}
        </div>

        {/* Web access: show info if already set */}
        {(hasWebAccess || webDone) && (
          <div className="ap-cabinet">
            <p className="ap-cabinet-desc">
              Вход: <b>{userEmail || webEmail}</b>
            </p>
            <div className="ap-cabinet-link">
              <span className="ap-cabinet-url">{cabinetLink}</span>
              <button className="ap-cabinet-copy" onClick={handleCopyLink}>
                {copiedLink ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>
        )}

        {/* Web access: set-password form */}
        {showWebForm && !webDone && !hasWebAccess && (
          <motion.div className="ap-email-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
            <input
              type="email"
              className="ap-email-input"
              placeholder="Email для входа"
              value={webEmail}
              onChange={e => { setWebEmail(e.target.value); setWebError(''); }}
            />
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="ap-email-input"
                placeholder="Пароль (мин. 6 символов)"
                value={webPassword}
                onChange={e => { setWebPassword(e.target.value); setWebError(''); }}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4 }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {webError && <span className="ap-email-error">{webError}</span>}
            <button className="ap-email-submit" onClick={handleSetWebAccess} disabled={webSaving}>
              {webSaving ? <Loader size={16} className="spin" /> : 'Создать веб-доступ'}
            </button>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8, textAlign: 'center' }}>
              На email придёт ссылка для подтверждения
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};
